/**
 * フロント側 /api/classify クライアント + 並列制御。
 *
 * - 1 枚ずつ: classifyFile(file) → ClassificationResult
 * - 80 枚一括: classifyAll(files, concurrency=10) → ClassificationResult[]
 *
 * 画像は classify 送信前に 1024×1024 に縮小してトークン節約 (CATEGORIES.md 推奨)。
 */

import type { ClassificationResult, ClassifyRequest, ClassifyResponse } from '@shared/types';

const CLASSIFY_MAX_EDGE = 1024;
const CLASSIFY_JPEG_QUALITY = 0.85;
const DEFAULT_CONCURRENCY = 10;

export interface ClassifierConfig {
  readonly fetchFn?: typeof fetch;
  readonly endpoint?: string;
  readonly concurrency?: number;
  /** File → 1024px base64 (テスト時に差し替え可能) */
  readonly resizeForClassify?: (file: File) => Promise<{ base64: string; mimeType: 'image/jpeg' }>;
}

export async function classifyFile(
  file: File,
  config: ClassifierConfig = {},
): Promise<ClassificationResult> {
  const fetchFn = config.fetchFn ?? fetch;
  const endpoint = config.endpoint ?? '/api/classify';
  const resize = config.resizeForClassify ?? defaultResizeForClassify;

  const { base64, mimeType } = await resize(file);
  const body: ClassifyRequest = { imageBase64: base64, mimeType };

  const res = await fetchFn(endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`POST /api/classify returned ${res.status}`);
  }
  const data = (await res.json()) as ClassifyResponse;
  // Wave 19 以降: carBoundingBox (外装写真のみ) をそのまま透過させる。
  // このフィールドが欠落すると smart crop が動かず中央クロップにフォールバック
  // する既知のバグ (Wave 21.4 で田中さん本番テストで発覚)。
  return {
    categoryId: data.categoryId,
    categoryName: data.categoryName,
    confidence: data.confidence,
    hasReflection: data.hasReflection,
    ...(data.carBoundingBox !== undefined ? { carBoundingBox: data.carBoundingBox } : {}),
  };
}

/**
 * 80 枚を並列 `concurrency` 本で分類。失敗したスロットは equipment_detail にフォールバック。
 * 順序は入力配列と一致 (返り値の index が同じ)。
 */
export async function classifyAll(
  files: readonly File[],
  config: ClassifierConfig = {},
): Promise<readonly ClassificationResult[]> {
  const concurrency = Math.max(1, config.concurrency ?? DEFAULT_CONCURRENCY);
  const results: ClassificationResult[] = new Array<ClassificationResult>(files.length);
  let nextIndex = 0;

  const runWorker = async (): Promise<void> => {
    while (true) {
      const index = nextIndex++;
      if (index >= files.length) return;
      const file = files[index];
      if (!file) continue;
      try {
        results[index] = await classifyFile(file, config);
      } catch (err) {
        console.warn('[classify] falling back for index', index, err);
        results[index] = {
          categoryId: 14,
          categoryName: 'equipment_detail',
          confidence: 0,
          hasReflection: false,
        };
      }
    }
  };

  const workers = Array.from({ length: Math.min(concurrency, files.length) }, () => runWorker());
  await Promise.all(workers);
  return results;
}

// ===== デフォルト resize (ブラウザ Canvas 依存) =========================

async function defaultResizeForClassify(
  file: File,
): Promise<{ base64: string; mimeType: 'image/jpeg' }> {
  const bitmap = await createImageBitmap(file);
  const { width, height } = fitWithin(bitmap.width, bitmap.height, CLASSIFY_MAX_EDGE);
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('OffscreenCanvas 2D context unavailable');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height, 0, 0, width, height);
  const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: CLASSIFY_JPEG_QUALITY });
  const base64 = await blobToBase64(blob);
  return { base64, mimeType: 'image/jpeg' };
}

function fitWithin(w: number, h: number, maxEdge: number): { width: number; height: number } {
  const scale = Math.min(maxEdge / w, maxEdge / h, 1);
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
  };
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}
